import React, { useState } from 'react';
import { 
  Modal, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress, 
  Alert,
  InputAdornment, // Importação adicionada
  IconButton // Importação adicionada
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility'; // Importação adicionada
import VisibilityOff from '@mui/icons-material/VisibilityOff'; // Importação adicionada
import ServiceAPI from '../../services/serviceapi';
import { toast } from 'react-toastify';
import { SecUtils } from '../../utils/secutils';

interface ModalTrocarSenhaProps {
  open: boolean;
  onClose: () => void;
  usuarioCodigo: number;
}

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
};

const ModalTrocarSenha: React.FC<ModalTrocarSenhaProps> = ({ open, onClose, usuarioCodigo }) => {
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Novo estado para controlar a visibilidade das senhas
  const [showPassword, setShowPassword] = useState(false); 

  const handleTrocarSenha = async () => {
    setError(null);

    if (!senhaAtual || !novaSenha || !confirmarSenha) {
      setError('Todos os campos são obrigatórios.');
      return;
    }
    if (novaSenha !== confirmarSenha) {
      setError('A nova senha e a confirmação não coincidem.');
      return;
    }
    if (novaSenha.length < 6) {
      setError('A nova senha deve ter no mínimo 6 caracteres.');
      return;
    }

    setLoading(true);
    try {
      const config = {
        url: `/usuario/alterarsenha`,
        data: {
          "senhaAtual" :  SecUtils.encode(senhaAtual),
          "senhaNova" : SecUtils.encode(novaSenha),
        },
        config: { printLoad: true },
      };

      await ServiceAPI.execNativeService(config);
      
      toast.success('Senha alterada com sucesso!');
      onClose();
    } catch (err: any) {
      console.error('Erro ao trocar senha:', err.response?.data?.message);
      // Assumindo que a API retorna uma mensagem de erro na resposta
      const apiError = err.response?.data?.message || 'Falha ao alterar a senha. Verifique a senha atual.';
      setError(apiError);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Limpa os estados ao fechar
    setSenhaAtual('');
    setNovaSenha('');
    setConfirmarSenha('');
    setError(null);
    setShowPassword(false); // Garante que o estado de visibilidade é resetado
    onClose();
  };

  const handleClickShowPassword = () => {
    setShowPassword((show) => !show);
  };

  // O adornment é o botão de olho que será repetido nos 3 campos
  const passwordVisibilityAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label="toggle password visibility"
        onClick={handleClickShowPassword}
        edge="end"
        disabled={loading}
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <Box display="flex" alignItems="center" gap={1}>
          <LockResetIcon color="primary" />
          <Typography variant="h6" component="h2" color="primary">
            Trocar Senha
          </Typography>
        </Box>
        
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label="Senha Atual"
          // Alterna entre 'text' (visível) e 'password' (oculta)
          type={showPassword ? 'text' : 'password'}
          size="small"
          fullWidth
          value={senhaAtual}
          onChange={(e) => setSenhaAtual(e.target.value)}
          disabled={loading}
          InputProps={{ // Adiciona o botão de visibilidade
            endAdornment: passwordVisibilityAdornment
          }}
        />
        <TextField
          label="Nova Senha"
          // Alterna entre 'text' (visível) e 'password' (oculta)
          type={showPassword ? 'text' : 'password'}
          size="small"
          fullWidth
          value={novaSenha}
          onChange={(e) => setNovaSenha(e.target.value)}
          disabled={loading}
          InputProps={{ // Adiciona o botão de visibilidade
            endAdornment: passwordVisibilityAdornment
          }}
        />
        <TextField
          label="Confirmar Nova Senha"
          // Alterna entre 'text' (visível) e 'password' (oculta)
          type={showPassword ? 'text' : 'password'}
          size="small"
          fullWidth
          value={confirmarSenha}
          onChange={(e) => setConfirmarSenha(e.target.value)}
          disabled={loading}
          InputProps={{ // Adiciona o botão de visibilidade
            endAdornment: passwordVisibilityAdornment
          }}
        />

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button variant="outlined" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleTrocarSenha} disabled={loading} color="primary" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LockResetIcon />}>
            {loading ? 'Aguarde...' : 'Alterar Senha'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalTrocarSenha;